import React, { useState } from 'react';
import { Drawer } from '@mui/material';
import { MeetingNote } from '../../types';
import Button, { IconButton } from '../ui/Button';
import RowActions from '../ui/RowActions';
import { actionIcon } from '../ui/actionIcons';
import ChipCarousel from '../common/ChipCarousel';
import {
  CaseNoteSummary,
  CONTENT_TYPE_FILTERS,
  ContentTypeFilter,
  SESSION_FILTER_UNASSIGNED,
  SessionRailFilter,
  TABLOU_LAYER_KEYS,
  filterNotesByContentType,
  filterNotesBySessionRail,
  formatNoteDateTime,
  sessionLabel,
  tabloulLayerLabel,
} from './meetingNotesUtils';
import { t } from '../../utils/translations';
import TabloulNoteBadge from './TabloulNoteBadge';
import ExpandCollapse from '../common/ExpandCollapse';


const CONTENT_TYPE_LABEL: Record<ContentTypeFilter, string> = {
  all: t.meetingNotes.contentTypeAll,
  note: t.meetingNotes.contentTypeNote,
  tablou: t.meetingNotes.contentTypeTablou,
};

/**
 * Second, independent filter next to the session rail — which kind of entry to show. A
 * segmented control rather than another row of chips: exactly three options, never scrolls, and
 * looking different from the session chips keeps the two filters from reading as one control.
 *
 * The white pill is one element that slides under the labels (`translateX`, in multiples of its
 * own width) rather than each button toggling its own background — an instant repaint reads as a
 * broken checkbox, not a toggle; the slide is what makes it read as one.
 */
const ContentTypeToggle: React.FC<{
  value: ContentTypeFilter;
  onChange: (value: ContentTypeFilter) => void;
}> = ({ value, onChange }) => {
  const activeIndex = CONTENT_TYPE_FILTERS.indexOf(value);
  return (
    <div className="relative mt-2 inline-flex w-52 rounded-lg bg-slate-100 p-0.5">
      <span
        aria-hidden="true"
        className="absolute bottom-0.5 left-0.5 top-0.5 rounded-[7px] bg-white shadow-sm motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out"
        style={{ width: 'calc(33.333% - 3px)', transform: `translateX(${activeIndex * 100}%)` }}
      />
      {CONTENT_TYPE_FILTERS.map((filter) => {
        const active = value === filter;
        return (
          <button
            key={filter}
            type="button"
            onClick={() => onChange(filter)}
            className={`relative z-10 flex-1 rounded-[7px] px-2.5 py-1 text-xs font-medium transition-colors ${
              active ? 'text-brand-800' : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {CONTENT_TYPE_LABEL[filter]}
          </button>
        );
      })}
    </div>
  );
};

interface MeetingNotesSessionRailProps {
  summary: CaseNoteSummary | null;
  sessionFilters: SessionRailFilter[];
  selectedFilter: SessionRailFilter | null;
  onSelectFilter: (filter: SessionRailFilter) => void;
  contentTypeFilter: ContentTypeFilter;
  onSelectContentTypeFilter: (filter: ContentTypeFilter) => void;
  onAddNote?: () => void;
  /** Only offered while the Tablou filter is active, and only for a session that doesn't have
   * one yet — a session keeps at most one tablou, reopened via its badge past that point. */
  onAddTablou?: () => void;
  onEditNote?: (note: MeetingNote) => void;
  onDeleteNote?: (note: MeetingNote) => void;
  /** Opens a tablou note in its structured editor — the plain one would desync it. */
  onOpenTablou?: (note: MeetingNote) => void;
  /** Defaults to allowed; apprentices may only touch their own notes. */
  canModifyNote?: (note: MeetingNote) => boolean;
  /** Shown instead of the add button when the pairing is finished. */
  readOnlyNotice?: string;
  /** Marks a note written by somebody shadowing this case. */
  isNoteByApprentice?: (note: MeetingNote) => boolean;
  variant?: 'panel' | 'drawer';
  open?: boolean;
  onClose?: () => void;
}

const MeetingNotesSessionRail: React.FC<MeetingNotesSessionRailProps> = ({
  summary,
  sessionFilters,
  selectedFilter,
  onSelectFilter,
  contentTypeFilter,
  onSelectContentTypeFilter,
  onAddNote,
  onAddTablou,
  onEditNote,
  onDeleteNote,
  onOpenTablou,
  canModifyNote,
  readOnlyNotice,
  isNoteByApprentice,
  variant = 'panel',
  open = false,
  onClose,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const isDrawer = variant === 'drawer';
  const notesForSession = summary ? filterNotesBySessionRail(summary.notes, selectedFilter) : [];
  const notes = filterNotesByContentType(notesForSession, contentTypeFilter);

  const content = !summary ? (
    <div className="flex flex-1 items-center justify-center p-8 text-center">
      <p className="text-sm text-slate-500">{t.meetingNotes.selectCase}</p>
    </div>
  ) : (
    <>
      <div className="shrink-0 border-b border-slate-200 px-4 pb-4 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              {summary.case.counseledName}
            </h2>
            <p className="mt-0.5 truncate text-sm text-slate-500">{summary.case.title}</p>
            <p className="mt-1 text-xs text-slate-500">
              {summary.noteCount}{' '}
              {summary.noteCount === 1
                ? t.meetingNotes.noteSingular
                : t.meetingNotes.notePlural}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {readOnlyNotice ? (
              <span className="text-xs text-slate-500">{readOnlyNotice}</span>
            ) : null}
            {contentTypeFilter === 'tablou'
              ? onAddTablou && (
                  <Button size="sm" variant="primary" onClick={onAddTablou}>
                    {t.cases.cockpit.addTabloulCta}
                  </Button>
                )
              : onAddNote && (
                  <Button size="sm" variant="primary" onClick={onAddNote}>
                    {t.cases.cockpit.addNoteCta}
                  </Button>
                )}
            {isDrawer && onClose && (
              <IconButton
                icon={actionIcon.close}
                size="md"
                label={t.common.close}
                onClick={onClose}
              />
            )}
          </div>
        </div>

        {sessionFilters.length > 0 && (
          <ChipCarousel
            className="mt-3"
            items={sessionFilters}
            selected={selectedFilter}
            onSelect={onSelectFilter}
            getLabel={(filter) =>
              filter === SESSION_FILTER_UNASSIGNED ? t.meetingNotes.noSession : `S${filter}`
            }
            previousLabel={t.meetingNotes.previousSessions}
            nextLabel={t.meetingNotes.nextSessions}
          />
        )}

        {summary.notes.length > 0 && (
          <ContentTypeToggle value={contentTypeFilter} onChange={onSelectContentTypeFilter} />
        )}
      </div>

      <div className="flex-1 space-y-3 overflow-auto px-4 py-4">
        {summary.notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-10 text-center">
            <p className="text-sm text-slate-500">{t.meetingNotes.noNotesMessage}</p>
          </div>
        ) : notesForSession.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-10 text-center">
            <p className="text-sm text-slate-500">{t.meetingNotes.noNotesForSession}</p>
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-10 text-center">
            <p className="text-sm text-slate-500">{t.meetingNotes.noNotesForContentType}</p>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {selectedFilter === SESSION_FILTER_UNASSIGNED
                ? t.meetingNotes.noSession
                : sessionLabel(
                    typeof selectedFilter === 'number' ? selectedFilter : null
                  )}
              {' · '}
              {notes.length}{' '}
              {notes.length === 1
                ? t.meetingNotes.noteSingular
                : t.meetingNotes.notePlural}
            </p>
            {notes.map((note) => {
              const expanded = expandedId === note.id;
              const long = note.content.length > 220;
              const preview = long
                ? note.content.slice(0, 220).trimEnd()
                : note.content;
              const rest = long ? note.content.slice(preview.length) : '';
              return (
                <article
                  key={note.id}
                  className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-slate-800">
                        {note.createdByName}
                        {isNoteByApprentice?.(note) ? (
                          <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-semibold text-fuchsia-800">
                            {t.meetingNotes.apprenticeBadge}
                          </span>
                        ) : null}
                        {note.tablou ? (
                          <TabloulNoteBadge
                            onOpen={onOpenTablou ? () => onOpenTablou(note) : undefined}
                          />
                        ) : null}
                      </p>
                      <p className="text-xs text-slate-500">
                        {formatNoteDateTime(note.createdAt)}
                      </p>
                    </div>
                    {(canModifyNote?.(note) ?? true) && (
                      <RowActions
                        onEdit={
                          onEditNote && !note.tablou ? () => onEditNote(note) : undefined
                        }
                        onDelete={onDeleteNote ? () => onDeleteNote(note) : undefined}
                      />
                    )}
                  </div>
                  {note.tablou ? (
                    // The layer label is what tells the two apart at a glance — plain text alone
                    // reads as one undifferentiated paragraph.
                    <div className="space-y-1.5 text-sm leading-relaxed text-slate-700">
                      {TABLOU_LAYER_KEYS.map((key) => {
                        const value = (note.tablou?.[key] ?? '').trim();
                        if (!value) return null;
                        return (
                          <p key={key}>
                            <span className="font-semibold text-slate-700">
                              {tabloulLayerLabel(key)}:{' '}
                            </span>
                            {value}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">
                      {preview}
                      {long && !expanded ? '…' : null}
                      <ExpandCollapse open={long && expanded}>{rest}</ExpandCollapse>
                    </div>
                  )}
                  {!note.tablou && long && (
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedId(expanded ? null : note.id)
                      }
                      className="mt-2 text-xs font-medium text-brand-600 hover:text-brand-700"
                    >
                      {expanded ? t.meetingNotes.showLess : t.meetingNotes.showMore}
                    </button>
                  )}
                </article>
              );
            })}
          </>
        )}
      </div>
    </>
  );

  if (isDrawer) {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          className: 'flex h-full w-full max-w-md flex-col sm:max-w-lg',
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <div className="flex min-h-[28rem] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {content}
    </div>
  );
};

export default MeetingNotesSessionRail;
